<script lang="ts">
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import { localizationInfoProviderToken, modalManagerToken } from "@/tokens";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import ReviewsTranslationsQuery from "@apps/reviews/queries/ReviewsTranslationsQuery";
    import VotesQuery from "@apps/reviews/queries/VotesQuery";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import ReviewsTranslationsService from "@apps/reviews/services/ReviewsTranslationsService";
    import VotesService from "@apps/reviews/services/VotesService";
    import { reviewsGalleryModalToken } from "@apps/reviews/tokens";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import {
        BehaviorSubject,
        combineLatest,
        filter,
        lastValueFrom,
        map,
        switchMap,
    } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import KeyPressHandler from "@components/KeyPressHandler/KeyPressHandler.svelte";
    import ProductContainer from "@components/ProductContainer.svelte";
    import UnavailableProductContainer from "@components/UnavailableProductContainer.svelte";

    import ReviewsGalleryContent from "./components/ReviewsGalleryContent.svelte";
    import ReviewsGalleryImageSlider from "./components/ReviewsGalleryImageSlider/ImageSlider.svelte";
    import ReviewsGalleryModal from "./components/ReviewsGalleryModal.svelte";
    import ReviewsGallerySliderArrows from "./components/ReviewsGallerySliderArrows.svelte";
    import ReviewsProductSection from "./components/ReviewsProductSection.svelte";
    import ReviewsUnavailableProductSection from "./components/ReviewsUnavailableProductSection.svelte";
    import ReviewsGalleryManager from "./ReviewsGalleryManager";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let reviewsTranslationsQuery: ReviewsTranslationsQuery;
    export let reviewsTranslationsService: ReviewsTranslationsService;
    export let dynamicImportManager: DynamicImportManager;
    export let afterWidgetInject: () => void;

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const votesService = container.resolve(VotesService);
    const votesQuery = container.resolve(VotesQuery);

    const localizationInfo = container.resolve(localizationInfoProviderToken);
    const modalManager = container.resolve(modalManagerToken);
    modalManager.addModal(reviewsGalleryModalToken);

    const activeReview$ = reviewsGalleryManager.activeReview$;
    const activeImageIndex$ = reviewsGalleryManager.activeImageIndex$;
    const reviewsCount$ = reviewsGalleryManager.selectReviewsCount();
    const widgetName$ = reviewsGalleryManager.widgetName$;
    const reviewsTranslations$ = reviewsTranslationsQuery.translations$;
    const translationsPendingIds$ = reviewsTranslationsQuery.pendingIds$;
    const isReviewTranslationsSupport$ =
        reviewsTranslationsQuery.isLocaleSupported$;
    let showTranslation$ = new BehaviorSubject<boolean>(false);

    const reviewTranslation$ = combineLatest([
        activeReview$,
        reviewsTranslations$,
    ]).pipe(
        map(([activeReview, translations]) => {
            if (activeReview) {
                const translation = translations.find(
                    (t) => t.reviewId === activeReview.id
                );
                return translation || null;
            }
            return null;
        })
    );

    const reviewVotes$ = activeReview$.pipe(
        filter(Boolean),
        switchMap((activeReview) =>
            votesQuery.selectByReviewId(activeReview.id)
        )
    );

    onMount(() => {
        const handleOpenReviewEvent = (event: any) => {
            reviewsGalleryManager.openReviewModal(event.detail);
        };

        const reviewsSettingsSubscription = reviewsSettings$.subscribe(
            (settings) => {
                if (
                    settings?.canUseTranslation &&
                    settings.isReviewsTranslationEnabled
                ) {
                    lastValueFrom(
                        reviewsTranslationsService.checkIfLocaleSupported({
                            locale: localizationInfo.getLanguageIsoCode(),
                        })
                    );
                }
            }
        );

        window.document.addEventListener(
            "gw-open-review-gallery",
            handleOpenReviewEvent
        );

        afterWidgetInject();

        return () => {
            reviewsSettingsSubscription.unsubscribe();
        };
    });

    const handleVote = (event: CustomEvent<{ reviewId: number }>) => {
        lastValueFrom(votesService.toggleVote(event.detail.reviewId));
    };

    const seeTranslation = (
        event: CustomEvent<{ reviewId: number; targetLanguage: string }>
    ) => {
        lastValueFrom(
            reviewsTranslationsService.seeTranslation({
                reviewId: event.detail.reviewId,
                targetLanguage: event.detail.targetLanguage,
            })
        );
    };

    const handleClose = () => {
        showTranslation$.next(false);
        reviewsGalleryManager.closeReviewModal();
    };

    const handlePrevReview = () => {
        showTranslation$.next(false);
        reviewsGalleryManager.prev();
    };

    const handleNextReview = () => {
        showTranslation$.next(false);
        reviewsGalleryManager.next();
    };

    $: showProductSection =
        $widgetName$ &&
        [
            REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET,
            REVIEWS_WIDGET_NAMES.REVIEWS_SLIDER_WIDGET,
            REVIEWS_WIDGET_NAMES.REVIEWS_DRAWER_WIDGET,
        ].includes($widgetName$);
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <ReviewsGalleryModal {modalManager} on:closeModal={handleClose}>
            <KeyPressHandler
                on:keydownArrowLeft={handlePrevReview}
                on:keydownArrowRight={handleNextReview}
                on:keydownEsc={handleClose}
            >
                <div class="gw-rv-gallery">
                    <div class="gw-rv-gallery__body">
                        {#if $activeReview$?.images.length}
                            <div class="gw-rv-gallery__body-left">
                                {#key $activeReview$.id}
                                    <ReviewsGalleryImageSlider
                                        reviewTitle={$activeReview$.title}
                                        images={$activeReview$.images}
                                        activeImageIndex={$activeImageIndex$}
                                    />
                                {/key}
                            </div>
                        {/if}

                        <div class="gw-rv-gallery__body-right">
                            {#if $activeReview$}
                                <ReviewsGalleryContent
                                    reviewVotes={$reviewVotes$}
                                    reviewsWithTitleEnabled={!!$reviewsSettings$?.isReviewTitleEnabled}
                                    review={$activeReview$}
                                    reviewTranslation={$reviewTranslation$}
                                    translationsPendingIds={$translationsPendingIds$}
                                    translationEnabled={($reviewsSettings$?.canUseTranslation &&
                                        $isReviewTranslationsSupport$) ||
                                        false}
                                    isIncentivizedEnabled={$reviewsSettings$?.isReviewsIncentivizedLabelEnabled}
                                    {showTranslation$}
                                    on:toggleVote={handleVote}
                                    on:close={handleClose}
                                    on:seeTranslation={seeTranslation}
                                >
                                    <svelte:fragment slot="footer">
                                        {#if showProductSection && $activeReview$?.product?.id}
                                            {#if $activeReview$.product?.isDeleted || $activeReview$.product?.isDraft}
                                                <UnavailableProductContainer
                                                    productId={$activeReview$
                                                        .product.id}
                                                    token={$activeReview$
                                                        .product
                                                        ?.unavailableProductToken ||
                                                        ""}
                                                    let:productInfo
                                                >
                                                    {@const product =
                                                        productInfo.product}
                                                    {#if product}
                                                        <ReviewsUnavailableProductSection
                                                            {product}
                                                        />
                                                    {/if}
                                                </UnavailableProductContainer>
                                            {:else}
                                                <ProductContainer
                                                    productId={$activeReview$
                                                        .product.id}
                                                    let:productInfo
                                                >
                                                    {@const product =
                                                        productInfo.product}
                                                    {@const productUrl =
                                                        productInfo.productUrl}
                                                    {#if product && productUrl}
                                                        <ReviewsProductSection
                                                            {product}
                                                            {productUrl}
                                                            {dynamicImportManager}
                                                        />
                                                    {/if}
                                                </ProductContainer>
                                            {/if}
                                        {/if}
                                    </svelte:fragment>
                                </ReviewsGalleryContent>
                            {/if}
                        </div>
                    </div>
                    {#if $reviewsCount$ > 1}
                        <ReviewsGallerySliderArrows
                            on:next={handleNextReview}
                            on:prev={handlePrevReview}
                        />
                    {/if}
                </div>
            </KeyPressHandler>
        </ReviewsGalleryModal>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-gallery {
        height: 100%;

        @media screen and (max-width: 800px) {
            flex-direction: column;
        }

        &__body {
            width: 100%;
            height: 100%;
            display: flex;
            overflow: hidden;
            background-color: #fff;

            @media screen and (max-width: 1000px) {
                flex-direction: column;
            }

            @media screen and (max-width: 600px) {
                padding: 50px 0 0;
            }

            &-left {
                width: 600px;
                background-color: #090916;

                @media screen and (max-width: 1200px) {
                    max-width: 430px;
                }
                @media screen and (max-width: 1000px) {
                    min-width: 100%;
                    max-width: 100%;
                    max-height: 300px;
                }

                @media screen and (max-width: 600px) {
                    box-sizing: border-box;
                    padding: 0 20px;
                    max-height: fit-content;
                    background-color: transparent;
                }
            }

            &-right {
                flex: 1;
                width: 400px;

                @media screen and (max-width: 1000px) {
                    width: 100%;
                }
            }
        }
    }
</style>
